// Copyright (c) Ping. All rights reserved.
//
// Self-contained time+tick pill + "Read by" popover for media tiles.
// Render this as a SIBLING to the card's main Pressable (not nested inside)
// so pointer events are captured cleanly on both web and native — nesting a
// button inside another button is invalid HTML and breaks tap routing on web.
//
// Usage (absolute overlay mode — image / video / doc):
//   <View>   ← positioning context (no explicit size needed; fills from Pressable)
//     <Pressable onPress={onTapOpen}>...</Pressable>
//     <ReadReceiptOverlay style={styles.timeOverlay} ... />
//   </View>
//
// Usage (inline mode — voice / location footer row):
//   <View style={styles.metaRow}>
//     <Text>{timestamp}</Text>
//     <ReadReceiptOverlay delivered={...} read={...} ... />   ← no absolute style
//   </View>

import { useRef, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Check, CheckCheck } from 'lucide-react-native';
import { tokens } from '../design-system/tokens';
import { ReadByPopover, type ReadByReader } from './ReadByPopover';

export type { ReadByReader };

export interface ReadReceiptOverlayProps {
  readonly timeLabel?: string;
  readonly delivered?: boolean;
  readonly read?: boolean;
  readonly isSelf: boolean;
  readonly readers?: ReadByReader[];
  /** Absolute-overlay position (image/video/doc). Omit for inline use. */
  readonly style?: StyleProp<ViewStyle>;
  readonly popoverAlign?: 'start' | 'center' | 'end';
  readonly popoverPlacement?: 'top' | 'bottom' | 'auto';
  /** Style for the time text. Defaults to white 10pt for dark overlays. */
  readonly textStyle?: StyleProp<object>;
  /** Color for the tick icon. Defaults to white/green. */
  readonly tickColor?: string;
  /** Whether to render the dark semi-transparent pill background. Set false
   *  for inline (voice/doc footer) use where the background is the bubble. */
  readonly showPill?: boolean;
}

export function ReadReceiptOverlay({
  timeLabel,
  delivered,
  read,
  isSelf,
  readers,
  style,
  popoverAlign = 'end',
  popoverPlacement = 'top',
  textStyle,
  tickColor,
  showPill = true,
}: ReadReceiptOverlayProps): JSX.Element | null {
  const tickRef = useRef<View | null>(null);
  const [readersOpen, setReadersOpen] = useState(false);
  const hasReaders = isSelf && (readers?.length ?? 0) > 0;

  const readColor = tickColor ?? '#3CCB4F';
  const deliveredColor = tickColor ?? '#FFFFFF';
  const sentColor = tickColor ?? 'rgba(255,255,255,0.7)';

  const tick =
    isSelf && (delivered === true || read === true) ? (
      <CheckCheck
        size={12}
        strokeWidth={2}
        color={read === true ? readColor : deliveredColor}
      />
    ) : isSelf ? (
      <Check size={12} strokeWidth={2} color={sentColor} />
    ) : null;

  const pillStyle = showPill ? styles.pill : styles.inline;

  return (
    <>
      {hasReaders ? (
        <Pressable
          ref={tickRef}
          style={[pillStyle, style]}
          onPress={() => setReadersOpen(true)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="See who read this message"
        >
          {timeLabel !== undefined ? (
            <Text style={[styles.timeText, textStyle]}>{timeLabel}</Text>
          ) : null}
          {tick}
        </Pressable>
      ) : (
        <View ref={tickRef} style={[pillStyle, style]} pointerEvents="none">
          {timeLabel !== undefined ? (
            <Text style={[styles.timeText, textStyle]}>{timeLabel}</Text>
          ) : null}
          {tick}
        </View>
      )}
      <ReadByPopover
        anchorRef={tickRef as { current: unknown }}
        open={readersOpen}
        onClose={() => setReadersOpen(false)}
        readers={readers ?? []}
        placement={popoverPlacement}
        align={popoverAlign}
      />
    </>
  );
}

const styles = StyleSheet.create({
  // Dark translucent pill for image/video overlays.
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  // No background — used inside voice/doc footer rows.
  inline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  timeText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
    color: '#FFFFFF',
  },
});
